import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, errorMessage } from '../../api';
import { Chip, Fold, UploadedChip, cardClasses, inputClasses, primaryButton, smallNeutral } from '../../components/ui';
import { blockedLabel, blockedText, equalizeOutcome, flipDiff, mergeOutcomes, missingSummary, nothingToEqualize, whenText } from '../../lib/equalize';
import { useScreenState } from '../../lib/screenState';
import { plural } from '../../lib/text';
import EqualizeDialog from './EqualizeDialog';
import ClientPicker from '../../dashboard/ClientPicker';

const ownerTip = 'Los updates de un cliente no se publican a otro.';

// Qué pasaría con cada update del cliente de referencia al igualar: lo dice el servidor, con el mismo plan que
// usa la vista previa. Cada renglón lleva su motivo.
const StateLabel = ({ item, source, target }) => {
    if (item.state === 'ignored') return <Chip>{target} lo tiene ignorado</Chip>;
    if (item.state === 'owner') return <Chip title={ownerTip}>Es de {item.owner}</Chip>;
    if (item.state === 'blocked') return <Chip tone="red" title={`No se publica: ${blockedText(item, source, target)}`}>{blockedLabel(item, target)}</Chip>;
    return <>
        <Chip tone="blue">Se puede publicar</Chip>
        {item.outdated && <Chip tone="amber" title={`En ${source} está desactualizado y llega igual: después hay que republicarlo.`}>desactualizado</Chip>}
    </>;
};

// `search` deja sólo los updates cuyo nombre lo contiene; el título dice cuántos se ven de cuántos.
export const DiffList = ({ title, items, emptyText, renderLabel, search }) => {
    const shown = search ? items.filter(item => item.name.toLowerCase().includes(search)) : items;
    return (
    <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{title} <span className="font-normal">· {shown.length === items.length ? items.length : `${shown.length} de ${items.length}`}</span></h4>
        <ul aria-label={title} className="border border-slate-200 rounded-lg divide-y divide-slate-100 text-sm">
            {items.length === 0 && <li className="px-3 py-2 text-slate-400">{emptyText}</li>}
            {items.length > 0 && shown.length === 0 && <li className="px-3 py-2 text-slate-400">Ninguno coincide con la búsqueda.</li>}
            {shown.map(item => (
                <li key={item.name} className="px-3 py-2 flex justify-between items-center gap-2">
                    <span className="font-mono truncate" title={item.name}>{item.name}</span>
                    <span className="flex-shrink-0 flex items-center gap-1.5">{item.uploaded_by_admin && <UploadedChip />}{renderLabel(item)}</span>
                </li>
            ))}
        </ul>
    </div>
    );
};

// Lo que pasó en el último igualado: queda escrito acá hasta el próximo, aunque el aviso ya se haya ido.
const LastResult = ({ last, onSeeInRepublish }) => (
    <div role="status" aria-label="Último igualado" className={`mb-4 p-3 rounded-lg border text-sm ${last.tone === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
        <strong>Último igualado, {whenText(last.at)}</strong> ({last.both ? `${last.source} y ${last.target}` : `${last.target} con ${last.source}`}, <span className="font-mono">{last.version}</span>): {last.text}{' '}
        {last.outdated > 0 && <><button type="button" onClick={() => onSeeInRepublish()} className="font-semibold text-zafiro-600 hover:underline">Ir a Republicación</button>{last.published.length + last.details.length > 0 && <span aria-hidden="true" className="mx-1.5 text-slate-400">·</span>}</>}
        {last.published.length + last.details.length > 0 && (
            <Fold about="el último igualado">
                {last.published.length > 0 && <span className="block mt-1">{plural(last.published.length, 'publicado', 'publicados')}:</span>}
                {last.published.map(name => <span key={name} className="block font-mono text-xs break-all">{name}</span>)}
                {last.details.length > 0 && <span className="block mt-1">Sin publicar:</span>}
                {last.details.map((detail, i) => <span key={i} className="block font-mono text-xs break-words">{detail.text}</span>)}
            </Fold>
        )}
    </div>
);

// Comparar dos clientes e igualar: publicarle a uno lo que le falta del otro, en cualquiera de los dos sentidos o en
// los dos a la vez, en dos pasos (vista previa y confirmación). Igualar nunca despublica.
export default function CompareClients({ version, clients, addToast, onChanged, onSeeInRepublish }) {
    const [storedSource, setSource] = useScreenState('clients.compare.source', '');
    const [storedTarget, setStoredTarget] = useScreenState('clients.compare.target', '');
    const [last, setLast] = useScreenState('clients.compare.last', null);
    // Si un cliente elegido ya no existe, se descarta.
    const existing = (id) => (clients.some(c => c.id === id) ? id : '');
    const source = existing(storedSource);
    // Nunca el mismo en los dos: cada selector deja de ofrecer al que está en el otro y, si aun así coinciden
    // (una elección guardada de antes), el segundo se suelta de verdad en vez de quedar elegido sin verse.
    const target = existing(storedTarget) === source ? '' : existing(storedTarget);
    useEffect(() => { if (storedTarget && storedTarget === storedSource) setStoredTarget(''); }, [storedSource, storedTarget, setStoredTarget]);
    // El cartel abierto: los sentidos que se igualan ([{ source, target }]), o null.
    const [previewing, setPreviewing] = useState(null);
    const [search, setSearch] = useScreenState('clients.compare.search', '');

    // La comparación es de UNA versión y de UN par de clientes: lo que se muestra es siempre de lo elegido ahora.
    const key = source && target ? `${version}|${source}|${target}` : '';
    const [loaded, setLoaded] = useState({ key: '', status: 'loading', diff: null });
    const wanted = useRef('');
    const compare = useCallback(async () => {
        wanted.current = key;
        if (!key) return;
        // Al volver a comparar lo mismo (después de igualar) las listas no se vacían mientras llega la respuesta.
        setLoaded(prev => (prev.key === key && prev.status === 'ready' ? prev : { key, status: 'loading', diff: null }));
        try {
            const diff = (await apiClient.get('/api/compare', { params: { version, a: source, b: target } })).data;
            if (wanted.current === key) setLoaded({ key, status: 'ready', diff });
        } catch (e) { if (wanted.current === key) setLoaded({ key, status: 'error', diff: null }); }
    }, [key, version, source, target]);
    useEffect(() => { compare(); }, [compare]);
    useEffect(() => { setPreviewing(null); }, [key]);
    const { status, diff } = loaded.key === key ? loaded : { status: 'loading', diff: null };

    // Iguala cada sentido confirmado, uno después del otro. Si ninguno publicó nada, devuelve lo que el servidor
    // rechazó (el cartel lo deja a la vista); si alguno publicó, el cartel se cierra y queda el último igualado.
    const equalize = async (parts) => {
        const outcomes = [];
        let rejected = null;
        for (const part of parts) {
            try {
                const res = await apiClient.post('/api/compare', { version, ...part });
                outcomes.push(equalizeOutcome(res.data, part.target));
            } catch (e) {
                const data = e.response?.data;
                if (!data) { addToast('No se pudo igualar. Volvé a comparar para ver cómo quedó.', 'error'); continue; }
                addToast(errorMessage(e), 'error');
                const details = [...(data.errors || []), ...(data.not_published || []).map(item => `${item.name}: ${item.reason}`)];
                rejected = { message: errorMessage(e), details: [...(rejected?.details || []), ...details] };
            }
        }
        if (outcomes.length === 0) return rejected;
        outcomes.forEach(outcome => addToast(outcome.text, outcome.tone));
        setLast({ at: new Date().toISOString(), version, source, target, both: previewing?.length > 1, ...mergeOutcomes(outcomes) });
        setPreviewing(null);
        compare();
        onChanged();
        return null;
    };

    const invert = () => { setSource(target); setStoredTarget(source); };
    // Los tres botones: a la derecha lo de la izquierda, a la izquierda lo de la derecha, y los dos a la vez.
    const forward = { source, target }, backward = { source: target, target: source };
    const canForward = Boolean(diff) && diff.publishable > 0;
    const canBackward = Boolean(diff) && diff.publishable_b > 0;
    // Por qué cada botón está gris, dicho debajo; el de los dos sentidos se describe con los dos porqués.
    const whyForward = diff && !canForward ? nothingToEqualize(diff, source, target) : '';
    const whyBackward = diff && !canBackward ? nothingToEqualize(flipDiff(diff), target, source) : '';
    const query = search.trim().toLowerCase();
    return (
        <div className={cardClasses}>
            <h3 className="font-semibold text-lg tracking-tight mb-4 text-slate-900">Comparar clientes <span className="font-normal text-slate-400">· <span className="font-mono text-base">{version}</span></span></h3>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                {/* Con buscador: hay decenas de clientes. Cada uno deja de ofrecer al que está elegido en el otro. */}
                <ClientPicker single label="Cliente" clients={clients.filter(c => c.id !== target)} selected={source} onChange={setSource} />
                <span aria-hidden="true" className="text-lg text-slate-400">⇄</span>
                <ClientPicker single label="Otro cliente" clients={clients.filter(c => c.id !== source)} selected={target} onChange={setStoredTarget} />
                <button type="button" onClick={invert} disabled={!source && !target} title="Cambia de lado a los dos clientes" className={smallNeutral}>Invertir</button>
                {key && <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar update…" aria-label="Buscar update en la comparación" className={`${inputClasses} ml-auto w-56`} />}
            </div>
            {/* Los botones están siempre que haya dos clientes: grises, y con el porqué debajo, cuando no hay nada para hacer. */}
            {diff && <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
                <button type="button" onClick={() => setPreviewing([forward])} disabled={!canForward} aria-describedby={whyForward ? 'equalize-why-forward' : undefined}
                    title={canForward ? `Le publica a ${target} lo de ${source} que le falta. Muestra antes qué se publicaría` : undefined} className={`${primaryButton} whitespace-nowrap`}>Igualar {target} con {source}…</button>
                <button type="button" onClick={() => setPreviewing([backward])} disabled={!canBackward} aria-describedby={whyBackward ? 'equalize-why-backward' : undefined}
                    title={canBackward ? `Le publica a ${source} lo de ${target} que le falta. Muestra antes qué se publicaría` : undefined} className={`${primaryButton} whitespace-nowrap`}>Igualar {source} con {target}…</button>
                <button type="button" onClick={() => setPreviewing([forward, backward].filter((_, i) => [canForward, canBackward][i]))} disabled={!canForward && !canBackward}
                    aria-describedby={canForward || canBackward ? undefined : 'equalize-why-forward equalize-why-backward'}
                    title={canForward || canBackward ? `A cada uno le publica lo del otro que le falta, en un solo paso. Muestra antes qué se publicaría` : undefined} className={`${primaryButton} whitespace-nowrap`}>Igualar los dos…</button>
                {(whyForward || whyBackward) && <p className="basis-full text-xs text-slate-500 text-right">
                    {whyForward && <span id="equalize-why-forward" className="block">{whyForward}</span>}
                    {whyBackward && <span id="equalize-why-backward" className="block">{whyBackward}</span>}
                </p>}
            </div>}
            {last && <LastResult last={last} onSeeInRepublish={onSeeInRepublish} />}
            {!source && !target && <p className="text-sm text-slate-500">Elegí dos clientes: la referencia y el que se quiere dejar igual.</p>}
            {(source || target) && !key && <p className="text-sm text-slate-500">Elegí el otro cliente para comparar.</p>}
            {key && status === 'loading' && <p role="status" className="text-sm text-slate-500">Comparando…</p>}
            {key && status === 'error' && (
                <div role="alert" className="flex items-center justify-between gap-3 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
                    <span>No se pudo comparar.</span>
                    <button type="button" onClick={compare} className={`${smallNeutral} bg-white`}>Reintentar</button>
                </div>
            )}
            {diff && <>
                <p className="mb-4 text-sm text-slate-600">{diff.common === 0 ? 'No tienen ningún update publicado en común.' : `Tienen ${plural(diff.common, 'update publicado', 'updates publicados')} en común.`} {missingSummary(diff, source, target)} {missingSummary(flipDiff(diff), target, source)}</p>
                <div className="grid md:grid-cols-2 gap-6">
                    <DiffList title={`Publicados sólo en ${source}`} items={diff.only_a} emptyText={`${target} tiene todo lo de ${source}.`} search={query}
                        renderLabel={item => <StateLabel item={item} source={source} target={target} />} />
                    <DiffList title={`Publicados sólo en ${target}`} items={diff.only_b} emptyText={`${source} tiene todo lo de ${target}.`} search={query}
                        renderLabel={item => <StateLabel item={item} source={target} target={source} />} />
                </div>
            </>}
            {previewing && <EqualizeDialog version={version} directions={previewing} onConfirm={equalize} onCancel={() => setPreviewing(null)} />}
        </div>
    );
}
